<template>
  <div class="list-shipping-services">
    <div class="list-shipping-services__header">
      <h3>Servicios</h3>
      <AppButton variant="primary" @click="openCreateModal">
        + Nuevo servicio
      </AppButton>
    </div>

    <AppTable
      :items="services"
      :fields="fields"
      :busy="loading"
      :show-empty="true"
      empty-text="No hay servicios configurados."
      primary-key="id">
      <template #cell(active)="{ value }">
        <span :class="['list-shipping-services__badge', value ? 'is-active' : 'is-inactive']">
          {{ value ? 'Activo' : 'Inactivo' }}
        </span>
      </template>
      <template #cell(actions)="{ item }">
        <div class="list-shipping-services__actions">
          <AppIconButton
            icon="edit"
            title="Editar"
            @click="openEditModal(item)" />
          <AppIconButton
            icon="delete"
            title="Eliminar"
            danger
            @click="confirmDelete(item)" />
        </div>
      </template>
      <template #table-busy>
        <div class="list-shipping-services__loading">
          Cargando servicios...
        </div>
      </template>
    </AppTable>

    <AppModal
      :value="createModalOpen"
      @input="createModalOpen = $event"
      title="Nuevo servicio">
      <ShippingServiceForm
        @cancel="createModalOpen = false"
        @submit="onCreate" />
    </AppModal>

    <AppModal
      :value="editModalOpen"
      @input="editModalOpen = $event"
      title="Editar servicio">
      <ShippingServiceForm
        v-if="selectedService"
        :service="selectedService"
        :editMode="true"
        @cancel="editModalOpen = false"
        @submit="onUpdate" />
    </AppModal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import MakeToastMixin from '../mixins/MakeToastMixin.vue'
import AppModal from '../UI/AppModal.vue'
import AppButton from '../UI/AppButton.vue'
import AppTable from '../UI/AppTable.vue'
import AppIcon from '../UI/AppIcon.vue'
import AppIconButton from '../UI/AppIconButton.vue'
import ShippingServiceForm from './ShippingServiceForm.vue'

export default {
  name: 'ListShippingServices',
  components: { AppModal, AppButton, AppTable, AppIcon, AppIconButton, ShippingServiceForm },
  mixins: [MakeToastMixin],
  data () {
    return {
      loading: false,
      createModalOpen: false,
      editModalOpen: false,
      selectedService: null,
      fields: [
        { key: 'code', label: 'Código' },
        { key: 'name', label: 'Nombre' },
        { key: 'order', label: 'Orden' },
        { key: 'active', label: 'Estado' },
        { key: 'actions', label: 'Acciones', thClass: 'list-shipping-services__actions-col' }
      ]
    }
  },
  computed: {
    ...mapGetters(['shippingServices']),
    services () {
      return Array.isArray(this.shippingServices) ? this.shippingServices : []
    }
  },
  created () {
    this.fetchData()
  },
  methods: {
    ...mapActions(['fetchShippingServices', 'deleteShippingService']),
    async fetchData () {
      this.loading = true
      try {
        await this.fetchShippingServices({ includeInactive: true })
      } catch (err) {
        this.makeDangerToast('No se pudieron cargar los servicios')
      } finally {
        this.loading = false
      }
    },
    openCreateModal () {
      this.createModalOpen = true
    },
    openEditModal (service) {
      this.selectedService = service
      this.editModalOpen = true
    },
    async onCreate (created) {
      this.createModalOpen = false
      this.makeSuccessToast(`Servicio "${created.name}" creado`)
      await this.fetchData()
    },
    async onUpdate (updated) {
      this.editModalOpen = false
      this.selectedService = null
      this.makeSuccessToast(`Servicio "${updated.name}" actualizado`)
      await this.fetchData()
    },
    async confirmDelete (service) {
      const ok = await this.confirmModal.confirm(
        `¿Eliminar definitivamente el servicio <strong>${service.name}</strong> (<code>${service.code}</code>)?<br>Se borrará también su configuración de precios en las tiendas. Esta acción no se puede deshacer.`,
        {
          title: 'Eliminar servicio',
          confirmButton: {
            text: 'Eliminar',
            variant: 'danger'
          }
        }
      )
      if (!ok) return
      try {
        const response = await this.deleteShippingService(service.id)
        if (response && response.status === 200) {
          this.makeSuccessToast('Servicio eliminado')
          await this.fetchData()
        } else if (response && response.data && response.data.error) {
          this.makeDangerToast(response.data.error)
        } else {
          this.makeDangerToast('No se pudo eliminar el servicio')
        }
      } catch (err) {
        this.makeDangerToast('Error al eliminar el servicio')
      }
    }
  }
}
</script>

<style scoped>
.list-shipping-services {
  width: 100%;
}

.list-shipping-services__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.list-shipping-services__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--app-text-color, #374151);
}

.list-shipping-services__loading {
  padding: 2rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
}

.list-shipping-services__badge {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.list-shipping-services__badge.is-active {
  background: #d1fae5;
  color: #065f46;
}

.list-shipping-services__badge.is-inactive {
  background: #fee2e2;
  color: #991b1b;
}

.list-shipping-services__actions-col {
  width: 110px;
}

.list-shipping-services__actions {
  display: flex;
  gap: 0.5rem;
}
</style>
